import React, { Component } from "react";
import { Navigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import DatePicker from "react-date-picker";
import {
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend,
  Chart as ChartJS,
} from "chart.js";
import {
  InputGroup,
  Form,
  Row,
  Table,
  Col,
  Card,
  Button,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaBox,
  FaUsers,
  FaShoppingCart,
  FaMoneyBill,
  FaFileExport,
  FaDownload,
} from "react-icons/fa";

import CartContext from "./CartContext";
import { LoadSpinner } from "./LoadSpinner";
import ElasticCartAd from "./ElasticCartAd";

ChartJS.register(CategoryScale, ArcElement, Tooltip, Legend);

export class HomeDashboard extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      dashboardSummary: {},
      orderSummary: null,
      revenueSummary: null,
      customerSummary: null,
      totalCustomerAccessSummary: null,
      topProductsSummary: null,
      purchaseOrderSummary: null,
      purchaseOrderExpensesSummary: null,
      rawMaterialPurchaseOrderSummary: null,
      rawMaterialPurchaseOrderExpensesSummary: null,
      customDate: new Date(),
      dateSelection: "Today",
      loadSpinnerOpen: false,
      showElasticCart: true,
      firstLoad: false,
    };
  }

  componentDidMount = async () => {
    window.scrollTo(0, 0);

    await this.CleanupOldCustomerOrders();

    if (this.context.prods.length === 0) {
      // setTimeout(() => this.setState({ showElasticCart: false }), 2000);
      setTimeout(() => {
        this.setState({ loadSpinnerOpen: false });
        this.setState({ showElasticCart: false });
        this.setState({ firstLoad: true }, this.LoadDashboard);
      }, 2000);
    } else {
      this.setState({ showElasticCart: false, loadSpinnerOpen: false }, () => {
        this.LoadDashboard();
      });
    }
  };

  CleanupOldCustomerOrders = async () => {
    if (!this.context.storeId) return;

    const url = `${process.env.REACT_APP_API}StoreManager/DeleteOldCustomerOrders/${this.context.storeId}/2`;
    try {
      await fetch(url, { method: "DELETE" });
    } catch (err) {
      console.error("DeleteOldCustomerOrders failed:", err);
    }
  };

  OnDateSelectionChange = (e) => {
    this.setState({ dateSelection: e.target.value }, this.LoadDashboard);
  };

  LoadDashboard = async () => {
    // Show loading spinner
    this.setState({ loadSpinnerOpen: true });

    // Call all fetch methods and wait for all to complete
    await Promise.all([
      this.GetDashboardSummary(),
      this.GetOrderSummary(),
      this.GetRevenueSummary(),
      this.GetCustomerSummary(),
      this.GetTopSellingProductsSummary(),
      this.GetPurchaseOrderSummary(),
      this.GetPurchasesOrderExpensesSummary(),
      this.GetRawMaterialPurchaseOrderSummary(),
      this.GetRawMaterialPurchasesOrderExpensesSummary(),
      this.GetTotalCustomerAccessSummary(),
    ]).catch((err) => {
      console.error("Error loading dashboard data:", err);
    });

    // Hide loading spinner once all data is loaded
    this.setState({ loadSpinnerOpen: false });
  };

  // --- Generic Fetch Helper ---
  fetchSummary = async (endpoint, stateKey, config = {}) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}Dashboard/${endpoint}/${
          this.context.storeId
        }/${this.state.dateSelection}/${this.context.GetTicks(
          this.state.customDate,
        )}`,
      );
      const data = await res.json();
      if (data.status !== 400) {
        if (config.datasets) Object.assign(data.datasets[0], config.datasets);
        this.setState({ [stateKey]: data });
      }
    } catch (err) {
      console.error(`${endpoint} error:`, err);
    }
  };

  // --- API Calls ---
  GetDashboardSummary = () =>
    this.fetchSummary("GetDashboardSummary", "dashboardSummary");
  GetOrderSummary = () =>
    this.fetchSummary("GetOrderSummary", "orderSummary", {
      datasets: {
        label: "Orders",
        borderColor: "rgb(0,192,0)",
        backgroundColor: "rgba(0,0,192,0.6)",
        fill: false,
        tension: 0.4,
      },
    });
  GetRevenueSummary = () =>
    this.fetchSummary("GetRevenueSummary", "revenueSummary", {
      datasets: {
        label: "Revenues",
        borderColor: "rgb(0,255,0)",
        backgroundColor: "rgba(0,200,0,0.6)",
        fill: false,
        tension: 0.4,
      },
    });
  GetCustomerSummary = () =>
    this.fetchSummary("GetCustomerSummary", "customerSummary", {
      datasets: {
        label: "Customers",
        borderColor: "rgb(255,0,0)",
        backgroundColor: "rgba(0,200,200,0.6)",
      },
    });
  GetTopSellingProductsSummary = () =>
    this.fetchSummary("GetTopSellingProductsSummary", "topProductsSummary", {
      datasets: {
        label: "Products",
        borderColor: "rgb(100,100,100)",
        backgroundColor: "rgba(0,0,100,0.6)",
      },
    });
  GetPurchaseOrderSummary = () =>
    this.fetchSummary("GetPurchaseOrderSummary", "purchaseOrderSummary", {
      datasets: {
        label: "Purchase Orders",
        borderColor: "rgb(255,0,0)",
        backgroundColor: "rgba(150,150,150,0.6)",
      },
    });
  GetPurchasesOrderExpensesSummary = () =>
    this.fetchSummary(
      "GetPurchasesOrderExpensesSummary",
      "purchaseOrderExpensesSummary",
      {
        datasets: {
          label: "Expenses",
          borderColor: "rgb(0,255,0)",
          backgroundColor: "rgba(255,0,0,0.6)",
        },
      },
    );
  GetRawMaterialPurchaseOrderSummary = () =>
    this.fetchSummary(
      "GetRawMaterialPurchaseOrderSummary",
      "rawMaterialPurchaseOrderSummary",
      {
        datasets: {
          label: "Raw Orders",
          borderColor: "rgb(255,0,0)",
          backgroundColor: "rgba(100,100,100,0.6)",
        },
      },
    );
  GetRawMaterialPurchasesOrderExpensesSummary = () =>
    this.fetchSummary(
      "GetRawMaterialPurchasesOrderExpensesSummary",
      "rawMaterialPurchaseOrderExpensesSummary",
      {
        datasets: {
          label: "Raw Expenses",
          borderColor: "rgb(0,255,0)",
          backgroundColor: "rgba(255,0,0,0.6)",
        },
      },
    );
  GetTotalCustomerAccessSummary = () =>
    this.fetchSummary(
      "GetTotalCustomerAccessSummary",
      "totalCustomerAccessSummary",
      {
        datasets: {
          label: "Access",
          borderColor: "rgb(255,0,0)",
          backgroundColor: "rgba(0,192,0,0.6)",
        },
      },
    );

  // --- Render ---
  renderKpiCard = (title, value, icon, color, trend) => (
    <Card className="shadow-sm border-0 rounded-3 h-100">
      <Card.Body className="d-flex justify-content-between align-items-center">
        <div>
          <Card.Title className="text-muted small">{title}</Card.Title>
          <h3 className={`fw-bold mb-0 text-${color}`}>{value}</h3>
          <small className={`text-${trend > 0 ? "success" : "danger"}`}>
            {trend > 0 ? "▲" : "▼"} {Math.abs(trend)}% vs last period
          </small>
        </div>
        <div className="bg-light p-3 rounded-circle">{icon}</div>
      </Card.Body>
    </Card>
  );

  render() {
    if (this.state.showElasticCart || !this.context.storeSettings)
      return (
        <>
          <ElasticCartAd></ElasticCartAd>
        </>
      );
    if (this.context.userData.loginStatus !== "active")
      return <Navigate to="/UserLogin" />;

    const summary = this.state.dashboardSummary || {};

    return (
      <div className="container-fluid m-0 p-3 bg-light min-vh-100">
        {this.state.loadSpinnerOpen && <LoadSpinner open="true" />}

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="bg-white text-dark p-3 shadow-sm rounded">
            <strong>Dashboard - Business Insight 360</strong>
          </h3>
          {/* <ButtonGroup>
            <Button variant="outline-secondary">
              <FaDownload /> Export CSV
            </Button>
            <Button variant="outline-secondary">
              <FaFileExport /> Export PDF
            </Button>
          </ButtonGroup> */}
        </div>
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 shadow-sm border-0 rounded-3">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">Business Name</div>
                <div className="fw-semibold fs-6">
                  {this.context.store.name}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 shadow-sm border-0 rounded-3">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">Store Expires On</div>
                <div className="fw-semibold fs-6 text-danger blink_me">
                  {this.context.GetInDateFormat(
                    new Date(this.context.store.storeEnd),
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 shadow-sm border-0 rounded-3">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">Product Type</div>
                <div className="fw-semibold fs-6">
                  {this.context.GetProductTypeNameById(
                    this.context.store.productTypeId,
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 shadow-sm border-0 rounded-3">
              <Card.Body className="text-center">
                <div className="text-muted small mb-1">License Model</div>
                <div className="fw-semibold fs-6">
                  {this.context.GetLicenseTypeNameById(
                    this.context.store.storeTypeId,
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Date Filters */}
        <Row className="align-items-center bg-white shadow-sm rounded p-3 mb-4">
          <Col xs={12} md={4}>
            <h5 className="fw-semibold">Summary</h5>
          </Col>
          <Col xs={12} md={5}>
            <InputGroup>
              <InputGroup.Text className="fw-semibold">
                Date Range
              </InputGroup.Text>
              <Form.Select
                value={this.state.dateSelection}
                onChange={this.OnDateSelectionChange}
              >
                <option value="Custom">Custom</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="Month">This Month</option>
                <option value="This Year">This Year</option>
                <option value="Last Year">Last Year</option>
              </Form.Select>
            </InputGroup>
          </Col>
          {this.state.dateSelection === "Custom" && (
            <Col xs={12} md={3}>
              <DatePicker
                format="dd-MMM-yy"
                value={this.state.customDate}
                onChange={(e) =>
                  this.setState({ customDate: e }, this.LoadDashboard)
                }
              />
            </Col>
          )}
        </Row>

        {/* KPI Cards */}
        <Row className="mb-4 g-3">
          <Col md={2} sm={6}>
            {this.renderKpiCard(
              "Products",
              summary.totalProducts || 0,
              <FaBox size={28} />,
              "primary",
              5,
            )}
          </Col>
          <Col md={2} sm={6}>
            {this.renderKpiCard(
              "Customers",
              summary.totalCustomers || 0,
              <FaUsers size={28} />,
              "info",
              -3,
            )}
          </Col>
          <Col md={2} sm={6}>
            {this.renderKpiCard(
              "Orders",
              summary.totalOrders || 0,
              <FaShoppingCart size={28} />,
              "warning",
              8,
            )}
          </Col>
          <Col md={3} sm={6}>
            {this.renderKpiCard(
              "Revenue",
              new Intl.NumberFormat(this.context.storeSettings?.defaultLocale, {
                style: "currency",
                currency: this.context.storeSettings?.defaultCurrency,
              }).format(summary.totalRevenue || 0),
              <FaMoneyBill size={28} className="text-success" />,
              "success",
              12,
            )}
          </Col>
          <Col md={3} sm={6}>
            {this.renderKpiCard(
              "Purchases",
              new Intl.NumberFormat(this.context.storeSettings?.defaultLocale, {
                style: "currency",
                currency: this.context.storeSettings?.defaultCurrency,
              }).format(summary.totalPurchaseAmount || 0),
              <FaMoneyBill size={28} className="text-danger" />,
              "danger",
              -6,
            )}
          </Col>
        </Row>
        <Row className="g-4">
          {/* Sales Orders */}
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title className="fw-semibold">
                  Sales Orders Trend
                </Card.Title>
                {this.state.orderSummary ? (
                  <Line
                    height={120}
                    data={this.state.orderSummary}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" },
                        tooltip: { mode: "index", intersect: false },
                      },
                      interaction: {
                        mode: "nearest",
                        axis: "x",
                        intersect: false,
                      },
                      scales: {
                        x: { grid: { display: false } },
                        y: { ticks: { beginAtZero: true } },
                      },
                    }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Revenue vs Purchases */}
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title className="fw-semibold">
                  Revenue vs Purchases
                </Card.Title>
                {this.state.revenueSummary &&
                this.state.purchaseOrderSummary ? (
                  <Bar
                    height={130}
                    data={{
                      labels: this.state.revenueSummary.labels,
                      datasets: [
                        {
                          type: "bar",
                          label: "Purchases",
                          data: this.state.purchaseOrderSummary.datasets[0]
                            .data,
                          backgroundColor: "rgba(220,53,69,0.6)",
                          borderColor: "rgba(220,53,69,1)",
                          borderWidth: 1,
                          yAxisID: "y",
                        },
                        {
                          type: "line",
                          label: "Revenue",
                          data: this.state.revenueSummary.datasets[0].data,
                          borderColor: "rgba(25,135,84,1)",
                          backgroundColor: "rgba(25,135,84,0.2)",
                          tension: 0.4,
                          borderWidth: 2,
                          yAxisID: "y1",
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "top" },
                        tooltip: { mode: "index", intersect: false },
                      },
                      interaction: {
                        mode: "nearest",
                        axis: "x",
                        intersect: false,
                      },
                      scales: {
                        y: {
                          type: "linear",
                          display: true,
                          position: "left",
                          title: { display: true, text: "Purchases" },
                        },
                        y1: {
                          type: "linear",
                          display: true,
                          position: "right",
                          grid: { drawOnChartArea: false },
                          title: { display: true, text: "Revenue" },
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {/* Charts */}
        <Row className="g-4">
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title>Total Sale Orders</Card.Title>
                {this.state.orderSummary ? (
                  <Bar
                    height={120}
                    data={this.state.orderSummary}
                    options={{ responsive: true }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title>Total Revenue</Card.Title>
                {this.state.revenueSummary ? (
                  <Bar
                    height={120}
                    data={this.state.revenueSummary}
                    options={{ responsive: true }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mt-2">
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title>Product Purchase Orders</Card.Title>
                {this.state.purchaseOrderSummary ? (
                  <Bar
                    height={120}
                    data={this.state.purchaseOrderSummary}
                    options={{ responsive: true }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title>Purchase Expenses</Card.Title>
                {this.state.purchaseOrderExpensesSummary ? (
                  <Bar
                    height={120}
                    data={this.state.purchaseOrderExpensesSummary}
                    options={{ responsive: true }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {this.context.storeSettings.isManufacturer && (
          <Row className="g-4 mt-2">
            <Col md={6}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Body>
                  <Card.Title>Raw Material Orders</Card.Title>
                  {this.state.rawMaterialPurchaseOrderSummary ? (
                    <Bar
                      height={120}
                      data={this.state.rawMaterialPurchaseOrderSummary}
                      options={{ responsive: true }}
                    />
                  ) : (
                    <div className="text-muted">No data available</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Body>
                  <Card.Title>Raw Material Expenses</Card.Title>
                  {this.state.rawMaterialPurchaseOrderExpensesSummary ? (
                    <Bar
                      height={120}
                      data={this.state.rawMaterialPurchaseOrderExpensesSummary}
                      options={{ responsive: true }}
                    />
                  ) : (
                    <div className="text-muted">No data available</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="g-4 mt-2">
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title>Top Selling Products</Card.Title>
                {this.state.topProductsSummary ? (
                  <Line
                    height={120}
                    data={this.state.topProductsSummary}
                    options={{ responsive: true }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body>
                <Card.Title>New Customers</Card.Title>
                {this.state.customerSummary ? (
                  <Bar
                    height={120}
                    data={this.state.customerSummary}
                    options={{ responsive: true }}
                  />
                ) : (
                  <div className="text-muted">No data available</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {this.context.storeSettings.showEcommerceModule == true ||
        this.context.storeSettings.showEcommerceModule == undefined ? (
          <>
            <Row className="g-4 mt-2">
              <Col>
                <Card className="shadow-sm border-0 rounded-3">
                  <Card.Body>
                    <Card.Title>Store Access (Customers)</Card.Title>
                    {this.state.totalCustomerAccessSummary ? (
                      <Bar
                        height={120}
                        data={this.state.totalCustomerAccessSummary}
                        options={{ responsive: true }}
                      />
                    ) : (
                      <div className="text-muted">No data available</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  }
}
